import './styles/global.css'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { routeTree } from './routeTree.gen'
import { queryClient } from './lib/queryClient'
import { restorePersistedCache, subscribePersistedCache } from './lib/persist'
import { initOfflineSync } from './lib/offline'
import './lib/outbox'

// Set up the Router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Restore scroll position on back/forward instead of staying put.
  scrollRestoration: true,
  // Optional: Pass the queryClient into the router context if you want to use
  // TanStack Router's built-in loaders to prefetch queries before navigation.
  context: {
    queryClient,
  },
})

// Register the router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function registerServiceWorker() {
  // ENABLE_SW is only defined for production builds (rspack.config.ts) — dev
  // must stay SW-free; HMR and worker caching fight each other.
  if (!process.env.ENABLE_SW || !('serviceWorker' in navigator)) return
  // A newly installed worker takes over immediately (skipWaiting +
  // clients.claim in sw.ts). Reload once so the page swaps the stale
  // precached shell for the new one instead of running it until the next
  // manual refresh. First visits have no controller yet and need no reload.
  let reloading = false
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return
      reloading = true
      window.location.reload()
    })
  }
  const register = () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('service worker registration failed:', err)
    })
  }
  // The awaited cache restore above may finish after the load event has
  // already fired — don't wait for an event that may never come.
  if (document.readyState === 'complete') register()
  else window.addEventListener('load', register, { once: true })
}

async function bootstrap() {
  // Hydrate the cache (and any paused offline mutations) before first render
  // so an offline boot starts with data instead of error states.
  await restorePersistedCache()

  const rootElement = document.getElementById('root')!
  if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement)
    root.render(
      <React.StrictMode>
        {/* QueryClientProvider must wrap the RouterProvider */}
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </React.StrictMode>,
    )
  }

  subscribePersistedCache()
  initOfflineSync()
  registerServiceWorker()
}

void bootstrap()
